import React from 'react';
import { Bug } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import WizardStepper, { type WizardStep } from './wizard-stepper';
import { oeffneMeldung } from '../../lib/testing/context';
import { setzeWorkflow, useMeldenVerfuegbar } from '../../lib/testing/workflow';
import { cn } from '../../lib/utils';

export interface WizardShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: React.ReactNode;
  description?: React.ReactNode;

  currentStep?: number;
  steps?: WizardStep[];

  children: React.ReactNode;

  // Footer buttons (controlled)
  onCancel?: () => void;
  cancelLabel?: string;

  onBack?: () => void;
  backLabel?: string;
  canBack?: boolean;

  onNext?: () => void;
  nextLabel?: string;
  canNext?: boolean;

  onPrimary?: () => void;
  primaryLabel?: string;
  canPrimary?: boolean;

  isLoading?: boolean;

  contentClassName?: string;
  bodyClassName?: string;

  // Optional: custom footer content (for non-step modals with multiple actions)
  footer?: React.ReactNode;
}

const WizardShell: React.FC<WizardShellProps> = ({
  open,
  onOpenChange,
  title,
  description,
  currentStep,
  steps,
  children,
  onCancel,
  cancelLabel = 'Abbrechen',
  onBack,
  backLabel = 'Zurück',
  canBack = true,
  onNext,
  nextLabel = 'Weiter',
  canNext = true,
  onPrimary,
  primaryLabel = 'Speichern',
  canPrimary = true,
  isLoading = false,
  contentClassName,
  bodyClassName,
  footer,
}) => {
  const totalSteps = steps?.length ?? undefined;
  const showStepper = typeof currentStep === 'number' && !!steps && steps.length > 0;
  const darfMelden = useMeldenVerfuegbar();
  // Nur ein einfacher Titel taugt als Name des Ablaufs; ein Knoten wäre im
  // Ticket unlesbar.
  const ablaufName = typeof title === 'string' ? title : undefined;

  // Solange der Assistent offen ist, weiss die Fehlermeldung, wo sie gemeldet
  // wird — sonst müsste der Nachsteller den ganzen Ablauf erst wiederfinden.
  React.useEffect(() => {
    if (!open || !ablaufName) return;
    setzeWorkflow({
      name: ablaufName,
      schritt: typeof currentStep === 'number' ? currentStep : undefined,
      schritte: totalSteps,
      schrittName:
        typeof currentStep === 'number' ? steps?.[currentStep - 1]?.label : undefined,
    });
    return () => setzeWorkflow(null);
  }, [open, ablaufName, currentStep, totalSteps, steps]);
  const handleCancel = () => {
    if (onCancel) return onCancel();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => {
          // Requirement: wizard must NOT close when clicking outside the modal
          e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          // extra safety: prevent close on pointer down outside
          e.preventDefault();
        }}
        className={cn(
          // fixed size (desktop), but responsive bounded
          'flex flex-col p-0 gap-0 w-[1440px] h-[1080px] max-w-[95vw] max-h-[95vh]',
          contentClassName
        )}
      >
        <div className="px-6 pt-6">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle>{title}</DialogTitle>
                {description ? <DialogDescription>{description}</DialogDescription> : null}
              </div>
              {/* Der schwebende Knopf liegt unter dem Overlay des Dialogs und ist
                  hier nicht erreichbar. Ihn darüber zu heben hiesse, die
                  Schaltflächen des Assistenten zu verdecken — deshalb steht er
                  in der Kopfzeile, wo ohnehin Platz ist. Gemeldet wird hier
                  nicht nur Kaputtes, sondern auch, was fehlt. */}
              {darfMelden ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5 text-muted-foreground"
                  onClick={() => oeffneMeldung()}
                  aria-label="Ticket zu diesem Ablauf erstellen"
                >
                  <Bug className="h-4 w-4" />
                  <span className="hidden sm:inline">Ticket erstellen</span>
                </Button>
              ) : null}
            </div>
          </DialogHeader>

          {showStepper ? (
            <WizardStepper currentStep={currentStep ?? 1} totalSteps={totalSteps ?? 1} steps={steps ?? []} />
          ) : null}
        </div>

        <div className={cn('flex-1 min-h-0 overflow-y-auto px-6', bodyClassName)}>
          {children}
        </div>

        <DialogFooter className="px-6 pb-6 pt-4">
          {footer ?? (
            <div className="flex w-full items-center justify-between gap-3">
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
                {cancelLabel}
              </Button>

              <div className="flex items-center gap-2">
                {onBack ? (
                  <Button type="button" variant="secondary" onClick={onBack} disabled={!canBack || isLoading}>
                    {backLabel}
                  </Button>
                ) : null}

                {onNext ? (
                  <Button type="button" onClick={onNext} disabled={!canNext || isLoading}>
                    {nextLabel}
                  </Button>
                ) : null}

                {onPrimary ? (
                  <Button type="button" onClick={onPrimary} disabled={!canPrimary || isLoading} loading={isLoading}>
                    {primaryLabel}
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WizardShell;


